import React from "react";
import { motion } from "framer-motion";
import { Sprout, ScanSearch, PackageCheck, Handshake } from "lucide-react";

const FEATURES = [
  {
    icon: Sprout,
    title: "Farm-Fresh Sourcing",
    body:
      "Direct pickups from estates in the Western Ghats — no middle layers, no aged stock.",
  },
  {
    icon: ScanSearch,
    title: "Quality Grading",
    body:
      "Every lot is size-graded, colour-sorted and cupped for oil, aroma and moisture.",
  },
  {
    icon: PackageCheck,
    title: "Export-Ready Packaging",
    body:
      "Vacuum-sealed, jute-lined or private-label options. Container-ready docs included.",
  },
  {
    icon: Handshake,
    title: "Transparent Trading",
    body:
      "One owner on WhatsApp. Clear pricing, honest lot details, no last-minute surprises.",
  },
];

export const WhyChooseUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative bg-forestDeep text-cream py-28 md:py-40 overflow-hidden"
    >
      {/* Decorative diagonal texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #FAF7F0 0 1px, transparent 1px 22px)",
        }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="font-sans text-[11px] uppercase tracking-[0.32em] text-gold/80 mb-6 flex items-center gap-3"
            >
              <span>§</span> Why choose us
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
              className="font-serif font-light text-5xl md:text-6xl lg:text-[5.25rem] leading-[0.98]"
            >
              Not the biggest. <br />
              <span className="italic text-gold">The most careful.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 font-sans text-cream/75 text-base md:text-[17px] leading-relaxed max-w-[46ch]"
            >
              We chose to stay small. It lets us know every lot personally —
              and it lets our buyers speak to the person who chose it.
            </motion.p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6 md:gap-8">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.1,
                  ease: [0.65, 0, 0.35, 1],
                }}
                className="group relative rounded-sm border border-cream/15 bg-cream/[0.03] backdrop-blur-sm p-7 md:p-8 hover:border-gold/50 hover:bg-cream/[0.06] transition-colors"
                data-testid={`why-feature-${i}`}
              >
                <span className="absolute top-5 right-5 font-serif italic text-gold/70 text-lg">
                  0{i + 1}
                </span>
                <div className="w-11 h-11 rounded-full border border-gold/40 flex items-center justify-center text-gold mb-6 group-hover:bg-gold group-hover:text-forestDeep transition-colors">
                  <f.icon size={18} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-cream">
                  {f.title}
                </h3>
                <p className="mt-3 font-sans text-cream/70 text-sm md:text-[15px] leading-relaxed max-w-[38ch]">
                  {f.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
